import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import pb from '../lib/pb';
import { ClientResponseError } from 'pocketbase';

type Mode = 'login' | 'register';

function parsePbError(e: any): { form?: string; fields: Record<string, string> } {
  const out = { form: undefined as string | undefined, fields: {} as Record<string, string> };

  // PB fel kan ligga i e.data.data eller e.data
  const root: any = e?.data?.data ?? e?.data ?? {};

  // övergripande meddelande
  out.form = (e?.data?.message || e?.message || 'Något gick fel.');

  // fältvisa fel vi bryr oss om
  const candidates = ['email', 'password', 'passwordConfirm', 'name', 'username'];
  for (const k of candidates) {
    const msg = root?.[k]?.message || root?.[k]?.msg || null;
    if (msg) out.fields[k] = msg;
  }
  return out;
}

export interface AuthModalProps {
  onClose: () => void;
  onSuccess: () => void;
  onSaveLocal?: () => void; // NEW: show “Save locally” only when provided
}

export default function AuthModal(props: AuthModalProps) {
  const { onClose, onSuccess, onSaveLocal } = props;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [mode, setMode] = useState<Mode>('login');
  const [busy, setBusy] = useState(false);

  // NYTT: fel
  const [formError, setFormError] = useState<string | undefined>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearErrors = () => {
    setFormError(undefined);
    setFieldErrors({});
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    setBusy(true);
    try {
      if (mode === 'login') {
        await pb.collection('users').authWithPassword(email.trim(), password);
      } else {
        await pb.collection('users').create({
          email: email.trim(),
          password,
          passwordConfirm,
        });
        // auto-login efter register
        await pb.collection('users').authWithPassword(email.trim(), password);
      }
      onSuccess();
    } catch (err: any) {
      // INTE alert – visa i UI
      const pbErr = err instanceof ClientResponseError ? parsePbError(err) : { form: err?.message ?? 'Fel', fields: {} };
      setFormError(pbErr.form);
      setFieldErrors(pbErr.fields);
      // console för dev
      console.warn('[AuthModal] PB error', err?.status, err?.url, err?.data || err);
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div className="auth-modal" role="dialog" aria-modal="true">
      <div className="modal-card">
        <div className="modal-header">
          <h3>{mode === 'login' ? 'Log in' : 'Create account'}</h3>
        </div>

        <form className="modal-body" onSubmit={onSubmit} noValidate>
          {/* övergripande fel */}
          {formError && (
            <div className="error-banner" role="alert">{formError}</div>
          )}

          <label htmlFor="auth-email">E-mail</label>
          <input
            id="auth-email"
            type="email"
            value={email}
            onChange={(ev) => { setEmail(ev.target.value); if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: '' }); }}
            autoComplete="email"
            disabled={busy}
            aria-invalid={!!fieldErrors.email}
            aria-describedby={fieldErrors.email ? 'auth-email-err' : undefined}
          />
          {fieldErrors.email && <div id="auth-email-err" className="field-error">{fieldErrors.email}</div>}

          <label htmlFor="auth-pass">Password</label>
          <input
            id="auth-pass"
            type="password"
            value={password}
            onChange={(ev) => { setPassword(ev.target.value); if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: '' }); }}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            disabled={busy}
            aria-invalid={!!fieldErrors.password}
            aria-describedby={fieldErrors.password ? 'auth-pass-err' : undefined}
          />
          {fieldErrors.password && <div id="auth-pass-err" className="field-error">{fieldErrors.password}</div>}

          {mode === 'register' && (
            <>
                <label htmlFor="auth-pass2">Confirm password</label>
              <input
                id="auth-pass2"
                type="password"
                value={passwordConfirm}
                onChange={(ev) => { setPasswordConfirm(ev.target.value); if (fieldErrors.passwordConfirm) setFieldErrors({ ...fieldErrors, passwordConfirm: '' }); }}
                autoComplete="new-password"
                disabled={busy}
                aria-invalid={!!fieldErrors.passwordConfirm}
                aria-describedby={fieldErrors.passwordConfirm ? 'auth-pass2-err' : undefined}
              />
              {fieldErrors.passwordConfirm && <div id="auth-pass2-err" className="field-error">{fieldErrors.passwordConfirm}</div>}
            </>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" disabled={busy}>
              {busy ? (mode === 'login' ? 'Logging in…' : 'Creating account…') : (mode === 'login' ? 'Log in' : 'Create account')}
            </button>
          </div>

          <div className="auth-switch">
            {mode === 'login' ? (
              <button type="button" onClick={() => { setMode('register'); clearErrors(); }} disabled={busy} className="linklike">
                No account? Create one
              </button>
            ) : (
              <button type="button" onClick={() => { setMode('login'); clearErrors(); }} disabled={busy} className="linklike">
                Have an account? Log in
              </button>
            )}
          </div>
        </form>

        {/* NYTT: tydlig avdelare + lokal spara-sektion (visas bara om onSaveLocal finns) */}
        {onSaveLocal && (
          <div
            className="local-save-block"
            style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #3a3a3a' }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Or save in browser</div>
            <button
              type="button"
              className="local-save-btn"
              onClick={onSaveLocal}
              title="Save this sprite locally on this device"
              style={{
                // liten, neutral knapp – ingen röd ram
                fontSize: 14,
                padding: '6px 12px',
                lineHeight: 1.1,
                borderRadius: 6,
                border: '1px solid #3a3a3a',
                background: '#222',
                color: 'var(--ink, #eee)',
                boxShadow: 'none',
                outline: 'none',
              }}
            >
              SAVE LOCALLY
            </button>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
              Stored in this browser only. Clearing site data will remove it.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // render to document.body to avoid stacking-context issues
  return ReactDOM.createPortal(modal, document.body);
}